import { useState, useEffect, useCallback } from 'react';
import { throttle } from 'lodash';
import Placerequest from './placerequest/placerequest';
import Selectbeneficiary from './SelectBeneficiary';
import Confirmation from './confirmation/confirmation';

import { Beneficiary } from '../interfaces/Beneficiary';
import { PaymentRequest } from '../interfaces/PaymentRequest';
import useBeneficiaries from '../../../../customhooks/beneficiaries/useBeneficiaries';
import { usePaymentpurposes } from '../../../../customhooks/paymentpurpose/usePaymentpurposes';
import useWallets from '../../../../customhooks/wallets/useWallets';
import CustomStepper from '../../../../components/CustomStepper';
import {
    getFXRate,
    FxTransactionTypes,
    GetFXRatePayload
} from '../../../../api/wallets/wallets';
import { getErrorMessage, openNotification } from '../../../../utils/helper';

const MakePayment = () => {
    const [conversionType, setConversionType] = useState(0);
    const [recipientChoosen, setRecipientChoosen] = useState<Beneficiary>(
        {} as Beneficiary
    );
    const [activeStep, setActiveStep] = useState(0);

    const [loadingRate, setLoadingRate] = useState(false);
    const [rate, setRate] = useState(0);

    const [currencyFrom, setCurrencyFrom] = useState('NGN');
    const [currencyTo, setCurrencyTo] = useState('USD');

    const [amountToSend, setAmountToSend] = useState(0);
    const [amountToReceive, setAmountToReceive] = useState(0);

    const [requestObject, setRequestObject] = useState<PaymentRequest>({
        convertAmount: 0.0,
        convertCurrency: 'NGN',
        receiveAmount: 0.0,
        receiveCurrency: 'USD',
        calcConvert: 0,
        calcReceive: 0
    });

    const throttledChange = useCallback(
        throttle((value, callbackFunction) => {
            callbackFunction(value);
        }, 2000),
        []
    );

    const {
        beneficiaries,
        isLoading: isBeneficiariesLoading,
        invalidateBeneficiaryCache: fetchBeneficiaries
    } = useBeneficiaries();

    const { paymentPurposes, loadingPaymentPurposes, fetchPaymentpurposes } =
        usePaymentpurposes();

    const { wallets, isLoading: isWalletsLoading } = useWallets();

    const steps = [
        {
            label: 'Enter amount to transfer',
            title: 'Step 1'
        },
        {
            label: 'Select Beneficiary',
            title: 'Step 2'
        },
        {
            label: 'Confirmation',
            title: 'Step 3'
        }
    ];

    const handleGetRate = async (
        label: 'amountToReceive' | 'amountToSend',
        value: number
    ) => {
        setLoadingRate(true);

        try {
            const payload = {
                currencyFrom,
                currencyTo,
                type:
                    currencyFrom === currencyTo
                        ? FxTransactionTypes.BENEFICIARY_FUNDING_DIRECT
                        : FxTransactionTypes.BENEFICIARY_FUNDING_CONVERSION,
                [label]: value
            } as unknown as GetFXRatePayload;

            const res = await getFXRate(payload);

            const { data } = res.data;

            if (label === 'amountToSend') {
                setAmountToReceive(data?.amountToReceive);
            } else {
                setAmountToSend(data?.amountToSend);
            }

            setRate(data?.rate);
            setLoadingRate(false);
        } catch (error) {
            setLoadingRate(false);
            openNotification(
                'error',
                getErrorMessage(error) || 'Something went wrong'
            );
        }
    };

    const updateConversionType = (value: number) => {
        setConversionType(value);
    };

    /**
     * Updates the value of the recipientChoosen variable
     * @param value Object
     */
    const updateRecipientChoosen = (value: any) => {
        setRecipientChoosen(value);
    };

    /**
     * Update number of step
     * @param activeStep number
     */
    const updateStep = (activeStep: number) => {
        setActiveStep(activeStep);
    };

    const updateRequestObject = (value: any) => {
        setRequestObject(value);
    };

    const handleAmountToSendChange = (amountToSend: number) => {
        setAmountToSend(amountToSend);

        if (!amountToSend) {
            setAmountToReceive(0);
        } else {
            throttledChange(amountToSend, () =>
                handleGetRate('amountToSend', amountToSend)
            );
        }
    };

    const handleAmountToReceiveChange = (amountToReceive: number) => {
        setAmountToReceive(amountToReceive);

        if (!amountToReceive) {
            setAmountToSend(0);
        } else {
            throttledChange(amountToSend, () =>
                handleGetRate('amountToReceive', amountToReceive)
            );
        }
    };

    useEffect(() => {
        setAmountToReceive(0);
        setAmountToSend(0);
        setRate(0);
    }, [currencyFrom, currencyTo]);

    const [rerunRateCall, setRerunRateCall] = useState(false);

    const updateReRunRateCall = (value: boolean) => {
        setRerunRateCall(value);
    };

    return (
        <div className='flex lg:justify-start justify-center w-full'>
            <div className='flex gap-10 max-w-[100vw] w-full'>
                <div className='px-4 lg:pb-4 max-w-[150px] mr-[80px] lg:block hidden'>
                    <CustomStepper
                        transSteps={steps}
                        steps={steps}
                        activeStep={activeStep}
                        onChange={(value: number) => {
                            setActiveStep(value);
                        }}
                    />
                </div>
                {activeStep === 0 && (
                    <Placerequest
                        {...{
                            currencyFrom,
                            setCurrencyFrom,
                            currencyTo,
                            setCurrencyTo,
                            amountToSend,
                            amountToReceive,
                            rate,
                            isFetchingRate: loadingRate,
                            activeStep,
                            updateStep,
                            conversionType,
                            wallets,
                            isWalletsLoading,
                            setAmountToSend: handleAmountToSendChange,
                            setAmountToReceive: handleAmountToReceiveChange
                        }}
                    />
                )}

                {activeStep === 1 && (
                    <Selectbeneficiary
                        {...{
                            currencyFrom,
                            currencyTo,
                            activeStep,
                            updateStep,
                            requestObject,
                            updateRequestObject,
                            conversionType,
                            updateRecipientChoosen,
                            updateConversionType,
                            beneficiaries,
                            isBeneficiariesLoading,
                            fetchBeneficiaries,
                            paymentPurposes,
                            loadingPaymentPurposes
                        }}
                    />
                )}

                {activeStep === 2 && (
                    <Confirmation
                        currencyFrom={currencyFrom}
                        currencyTo={currencyTo}
                        amountToSend={amountToSend}
                        amountToReceive={amountToReceive}
                        updateStep={updateStep}
                        requestObject={requestObject}
                        conversionType={conversionType}
                        rate={rate}
                        recipientChoosen={recipientChoosen}
                        sourceWallet={wallets.find(
                            (item) =>
                                item.currency === requestObject.convertCurrency
                        )}
                    />
                )}
            </div>
        </div>
    );
};

export default MakePayment;
